import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewChecked,
  OnInit,
  OnDestroy
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';
import { ChatSessionService } from '../../services/chat-session.service';
import { ChatUiStateService } from '../../services/chat-ui-state.service';
import { ChatMessage, ChatUiState } from '../../models/chat.models';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { ChatTypingIndicatorComponent } from '../chat-typing-indicator/chat-typing-indicator.component';
import { ChatInputComponent } from '../chat-input/chat-input.component';

@Component({
  selector: 'app-chat-panel',
  standalone: true,
  imports: [ChatMessageComponent, ChatTypingIndicatorComponent, ChatInputComponent],
  templateUrl: './chat-panel.component.html',
  styleUrls: ['./chat-panel.component.scss']
})
export class ChatPanelComponent implements OnInit, AfterViewChecked, OnDestroy {

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef<HTMLDivElement>;

  messages: ChatMessage[] = [];
  uiState!: ChatUiState;

  private shouldScrollToBottom = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private session: ChatSessionService,
    private uiStateService: ChatUiStateService
  ) {}

  ngOnInit(): void {
    this.session.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(msgs => {
        this.messages = msgs;
        this.shouldScrollToBottom = true;
      });

    this.uiStateService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.uiState = state;
        // Scroll down when loading starts so the typing indicator is visible
        if (state.isLoading) {
          this.shouldScrollToBottom = true;
        }
      });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Actions ──────────────────────────────────────────────────────

  onSend(text: string): void {
    this.session.sendMessage(text);
  }

  close(): void {
    this.uiStateService.close();
  }

  newConversation(): void {
    this.session.newConversation();
  }

  dismissError(): void {
    this.uiStateService.setError(null);
  }

  trackByMessageId(_index: number, msg: ChatMessage): string {
    return msg.id;
  }

  // ─── Private ──────────────────────────────────────────────────────

  private scrollToBottom(): void {
    try {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    } catch { /* noop */ }
  }
}
